/**
 * Create DO from Plan — popup mở sau nút "Save vs DO" (S-Instruction & Direct P↔S).
 *
 * Convert từ mockup DoMockupPage (feedback ① 2026-06-04 — 2 chỗ tạo DO từ Plan):
 *   - scenario 'instruct': To WH default = kho TLC vừa instruct (receive_warehouse).
 *   - scenario 'direct':   To WH default = Shipper (Supplier) của HĐ P vừa match.
 * Draft table: Sale No | To WH (edit) | DO No (edit) | DO Qty (edit) | Note (edit).
 *
 * Phase FE-first (chốt 2026-06-12): nút [Save DO] DISABLED — bảng/endpoint DO
 * chưa chốt; khi backend sẵn sàng wire mutation vào nút này. [Cancel] đóng
 * popup, user book DO sau.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useMemo, useState } from 'react'

import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'

import {
  useCreateSaleDeliveryOrderMutation,
  useDoLookupWarehousesQuery,
  useLazySuggestDoNoQuery,
} from '../../../store/api/saleControlApi'
import {
  useUpdateDoFromPlanMutation,
  useDeleteDoFromPlanMutation,
} from '../../../store/api/matchingPsApi'
import { useDispatch } from 'react-redux'

import { apiSlice } from '../../../store/api/apiSlice'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { TEXT_TRUNCATE } from '@/theme/responsive'

/** Copper/brown — accent DO (đồng bộ mockup, tách màu với P #1e88e5 / S #fb8c00). */
export const ACCENT_DO = '#6d4c41'

export type DoScenario = 'instruct' | 'direct'

/** 1 dòng draft DO seed từ các plan row vừa save thành công. */
export interface DoDraftSeed {
  /** Sale contract no (read-only trong draft). */
  saleNo: string
  /** Buyer của Sale contract — hiển thị phụ, không edit. */
  buyer: string
  /** To WH default theo scenario — edit được. */
  toWh: string
  /** KG vừa instruct/match — edit được. */
  doQty: number
  /**
   * Dòng Plan nguồn tạo DO này — read-only, dòng CHÍNH (đậm) của cột From Plan:
   *   - direct:   HĐ Purchase được match (P contract no).
   *   - instruct: kho TLC vừa instruct (warehouse/factory).
   */
  planRef: string
  /** Dòng phụ (caption) của cột From Plan — vd Supplier name; '' nếu không có. */
  planRefSub: string
  /** KG theo plan đã save (match_kg) — đối chiếu khi user chỉnh DO Qty. */
  planKg: number
  /** Loại match nguồn ('direct' | 'instruct') — lưu vào delivery_order_scs.match_type. */
  matchType: string
  /** match_id của plan vừa save — lưu vào delivery_order_scs.match_id. */
  matchId: number | null
  /**
   * From WH có cho edit không (override scenario dialog-level). Direct = true
   * (Shipper, gõ tay); Instruct = false (kho TLC, read-only). undefined → theo
   * scenario. Dùng cho popup trộn nhiều loại (Edit Sale: direct + BL + NT).
   */
  fromWhEditable?: boolean
}

/**
 * 1 dòng DO trong block "Add DO" — KHÔNG còn 1-1 với plan: nhiều dòng DO có thể
 * cùng trỏ về 1 plan (tách 1 plan thành nhiều DO). Liên kết plan qua `planId`.
 */
interface DoDraftRow {
  id: number
  /** Index của plan nguồn (vị trí trong `seeds`) mà dòng DO này thuộc về. */
  planId: number
  /** delivery_order_scs_id khi là DO đã book (edit mode); null = dòng mới sẽ book. */
  doId: number | null
  /** do_no đã book (edit mode) — read-only; '' = sẽ book mới khi Save. */
  doNo: string
  toWh: string
  doQty: number
  note: string
}

/** 1 DO đã book — seed cho edit mode (mở từ "Edit vs DO" / sau Save Edit Plan). */
export interface EditDoRow {
  doId: number
  doNo: string
  toWh: string
  doQty: number
  note: string
  /** Index của plan (trong `seeds`) mà DO này thuộc về. Mặc định 0 (popup 1 plan). */
  planId?: number
}

export interface CreateDoFromPlanDialogProps {
  open: boolean
  scenario: DoScenario
  seeds: DoDraftSeed[]
  onClose: () => void
  /** 'create' (mặc định) = book DO mới; 'edit' = sửa DO đã book theo match. */
  mode?: 'create' | 'edit'
  /** Edit mode: các DO đã book (1 plan) để hydrate block Add DO. */
  editRows?: EditDoRow[]
}

const fmtNum = (n: number) => (n ? Math.round(n).toLocaleString('en-US') : '')

const cellInputSx = {
  // !important override iOS zoom fix global (16px @media xs, theme CssBaseline)
  // — input nằm trong cell table cột hẹp, 16px làm text wrap + hàng phình cao.
  '& .MuiInputBase-input': { fontSize: '0.72rem !important', py: 0.25 },
} as const

export default function CreateDoFromPlanDialog({
  open,
  scenario,
  seeds,
  onClose,
  mode = 'create',
  editRows,
}: CreateDoFromPlanDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      {/* Mount body on open → reset draft từ seeds mỗi lần mở. */}
      {open ? (
        <CreateDoBody
          scenario={scenario}
          seeds={seeds}
          onClose={onClose}
          mode={mode}
          editRows={editRows}
        />
      ) : null}
    </Dialog>
  )
}

function CreateDoBody({
  scenario,
  seeds,
  onClose,
  mode,
  editRows,
}: {
  scenario: DoScenario
  seeds: DoDraftSeed[]
  onClose: () => void
  mode: 'create' | 'edit'
  editRows?: EditDoRow[]
}) {
  // Init rows:
  // - Edit mode: per-plan → plan có DO cũ (editRows theo planId) thì load DO đó
  //   (reuse); plan chưa có DO thì 1 dòng default full qty. (Đa-plan: Edit Sale.)
  // - Create mode: 1 dòng / plan (full qty).
  const buildInitialRows = (): DoDraftRow[] => {
    if (mode === 'edit') {
      const out: DoDraftRow[] = []
      let id = 0
      seeds.forEach((s, planIdx) => {
        const planEdits = (editRows ?? []).filter((e) => (e.planId ?? 0) === planIdx)
        if (planEdits.length > 0) {
          for (const e of planEdits) {
            out.push({
              id: id++,
              planId: planIdx,
              doId: e.doId,
              doNo: e.doNo,
              toWh: e.toWh,
              doQty: e.doQty,
              note: e.note,
            })
          }
        } else {
          // Plan chưa có DO → 1 dòng default để book ngay.
          out.push({
            id: id++,
            planId: planIdx,
            doId: null,
            doNo: '',
            toWh: s.toWh,
            doQty: s.doQty,
            note: '',
          })
        }
      })
      return out
    }
    return seeds.map((s, i) => ({
      id: i,
      planId: i,
      doId: null,
      doNo: '',
      toWh: s.toWh,
      doQty: s.doQty,
      note: '',
    }))
  }
  const [rows, setRows] = useState<DoDraftRow[]>(buildInitialRows)
  // Counter id tăng dần cho row mới — khởi tạo theo số dòng init thực tế.
  const [nextId, setNextId] = useState(() => buildInitialRows().length)

  const patch = (id: number, key: keyof DoDraftRow, value: string | number) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)))

  const addRow = (planId: number) => {
    setRows((prev) => {
      // DO Qty mặc định = Remaining của plan (Plan Qty − đã allocate), tối thiểu 0.
      const allocated = prev
        .filter((r) => r.planId === planId)
        .reduce((sum, r) => sum + (r.doQty || 0), 0)
      const remaining = Math.max(0, (seeds[planId]?.planKg ?? 0) - allocated)
      return [
        ...prev,
        {
          id: nextId,
          planId,
          doId: null,
          doNo: '',
          toWh: seeds[planId]?.toWh ?? '',
          doQty: remaining,
          note: '',
        },
      ]
    })
    setNextId((n) => n + 1)
  }
  // Edit mode: DO đã book bị bỏ dòng → ghi nhận để soft-delete khi Save.
  const [removedDoIds, setRemovedDoIds] = useState<number[]>([])
  const removeRow = (id: number) =>
    setRows((prev) => {
      const target = prev.find((r) => r.id === id)
      if (target?.doId) setRemovedDoIds((ids) => [...ids, target.doId as number])
      return prev.filter((r) => r.id !== id)
    })

  // From WH options — dùng chung lookup với form New Delivery Order
  // (GET /sale-delivery-orders/lookups/warehouses). Gộp thêm default seed để
  // value đã set vẫn hiện kể cả khi không nằm trong danh sách warehouse.
  const whQuery = useDoLookupWarehousesQuery()
  const whOptions = useMemo(() => {
    const fromApi = (whQuery.data?.items ?? []).map((it) => it.label)
    const fromSeed = seeds.map((s) => s.toWh).filter(Boolean)
    return Array.from(new Set([...fromApi, ...fromSeed]))
  }, [whQuery.data, seeds])
  const totalQty = useMemo(() => rows.reduce((s, r) => s + (r.doQty || 0), 0), [rows])
  // KG đã phân bổ vào DO theo từng plan → so với plan qty (còn lại / vượt).
  const allocatedByPlan = useMemo(() => {
    const m = new Map<number, number>()
    for (const r of rows) m.set(r.planId, (m.get(r.planId) ?? 0) + (r.doQty || 0))
    return m
  }, [rows])
  // Total DO Qty phải ĐÚNG BẰNG Plan Qty mỗi plan mới cho Save (diff = allocated −
  // planKg; diff>0 lố, diff<0 thiếu). diff ≠ 0 → chặn Save + cảnh báo.
  const unbalancedPlans = useMemo(
    () =>
      seeds
        .map((s, i) => ({ s, diff: (allocatedByPlan.get(i) ?? 0) - s.planKg }))
        .filter((x) => x.diff !== 0),
    [seeds, allocatedByPlan],
  )
  const planColLabel = scenario === 'direct' ? 'From Plan (Purchase)' : 'From Plan (Instruction)'

  // Save DO — create mode: book mới (do_no auto suggest tuần tự, mang match_type/id).
  // Edit mode: dòng có doId → partial update (wh/qty/note); dòng mới → book mới;
  // dòng bỏ → soft-delete. Không đụng field khác của DO khi update.
  const snack = useSnackbar()
  const dispatch = useDispatch()
  const [suggestDo] = useLazySuggestDoNoQuery()
  const [createDo, { isLoading: creatingDo }] = useCreateSaleDeliveryOrderMutation()
  const [updateDo, { isLoading: updatingDo }] = useUpdateDoFromPlanMutation()
  const [deleteDo, { isLoading: deletingDo }] = useDeleteDoFromPlanMutation()
  const savingDo = creatingDo || updatingDo || deletingDo
  const today = new Date().toISOString().slice(0, 10)

  // Mọi dòng DO phải có DO Qty > 0.
  const hasNonPositiveQty = rows.some((r) => !(r.doQty > 0))
  const canSaveDo =
    unbalancedPlans.length === 0 &&
    !hasNonPositiveQty &&
    !savingDo &&
    rows.length > 0
  const handleSaveDo = async () => {
    if (rows.length === 0) {
      snack.warning('Add at least one DO row.')
      return
    }
    if (hasNonPositiveQty) {
      snack.warning('DO Qty must be greater than 0 for every row.')
      return
    }
    if (unbalancedPlans.length > 0) {
      snack.warning('Total DO Qty must equal Plan Qty for every plan.')
      return
    }
    try {
      let touched = 0
      for (const r of rows) {
        const plan = seeds[r.planId]
        if (r.doId) {
          // DO đã book → partial update.
          await updateDo({
            doId: r.doId,
            fromWarehouse: r.toWh || null,
            doQty: r.doQty || null,
            note: r.note || null,
          }).unwrap()
        } else {
          const idx = await suggestDo().unwrap().catch(() => null)
          await createDo({
            doNo: idx ? `${idx.index}TNC` : null,
            doDate: today,
            saleContract: plan?.saleNo || null,
            fromWarehouse: r.toWh || null,
            netWeightKg: r.doQty || null,
            bookingNote: r.note || null,
            matchType: plan?.matchType || null,
            matchId: plan?.matchId ?? null,
          }).unwrap()
        }
        touched += 1
      }
      // Soft-delete các DO đã bỏ dòng (edit mode).
      for (const doId of removedDoIds) {
        await deleteDo({ doId }).unwrap()
      }
      // Refresh các bảng trên Form: detail grid Matching (do_lst/do_done qua view)
      // + list Delivery Order. createSaleDeliveryOrder không tự invalidate MatchingPs.
      dispatch(apiSlice.util.invalidateTags(['MatchingPs', 'SaleDeliveryOrder']))
      snack.success(
        mode === 'edit'
          ? 'Delivery Order(s) updated.'
          : `${touched} Delivery Order(s) booked.`,
      )
      onClose()
    } catch (err: unknown) {
      const msg =
        (err as { data?: { detail?: string } })?.data?.detail ??
        (err as { message?: string })?.message ??
        'Failed to save DO'
      snack.error(msg)
    }
  }

  return (
    <>
      <DialogTitle
        component="div"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, pr: 1 }}
      >
        <Chip
          size="small"
          icon={<AddBusinessOutlinedIcon sx={{ fontSize: 15, color: '#fff !important' }} />}
          label="DO"
          sx={{ bgcolor: ACCENT_DO, color: '#fff', fontWeight: 700, height: 22 }}
        />
        <Typography
          variant="subtitle1"
          noWrap
          title={`${mode === 'edit' ? 'Edit DO of ' : 'Create DO from '}${
            scenario === 'direct' ? 'Direct P-S match' : 'S-Instruction'
          }`}
          sx={{ ...TEXT_TRUNCATE, fontWeight: 800, flex: 1, minWidth: 0 }}
        >
          {mode === 'edit' ? 'Edit DO of ' : 'Create DO from '}
          {scenario === 'direct' ? 'Direct P-S match' : 'S-Instruction'}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1.5 }}>
        <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
          <Typography variant="caption">
            Use <b>+ DO</b> to split a plan; edit <b>DO Qty</b> & <b>From WH</b> per row.
          </Typography>
        </Alert>

        {/* ───────── Block 1: View Plan (read-only) ───────── */}
        <Typography
          variant="overline"
          sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.5 }}
        >
          Plan (source)
        </Typography>
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
          {/* minWidth = tổng width cột khai bên dưới — thiếu nó Table co theo dialog
              hẹp (xs) làm cột/input bị bóp nát thay vì scroll ngang trong container. */}
          <Table size="small" sx={{ minWidth: 940, '& .MuiTableCell-root': { py: 0.25 } }}>
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    fontWeight: 800,
                    fontSize: '0.7rem',
                    whiteSpace: 'nowrap',
                    bgcolor: alpha(ACCENT_DO, 0.07),
                  },
                }}
              >
                <TableCell sx={{ width: 140 }}>Sale No</TableCell>
                <TableCell sx={{ width: 200 }}>{planColLabel}</TableCell>
                <TableCell sx={{ width: 110 }}>Match Type</TableCell>
                <TableCell sx={{ width: 90 }}>Match ID</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>Plan Qty</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>Allocate to DO</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>Remaining</TableCell>
                <TableCell sx={{ width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {seeds.map((s, i) => {
                const allocated = allocatedByPlan.get(i) ?? 0
                const remaining = s.planKg - allocated
                const remColor =
                  remaining < 0 ? 'error.main' : remaining > 0 ? 'warning.main' : 'success.main'
                return (
                  <TableRow key={i}>
                    <TableCell sx={{ verticalAlign: 'middle' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.74rem' }}>
                        {s.saleNo}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.64rem' }}
                      >
                        {s.buyer}
                      </Typography>
                    </TableCell>
                    {/* From Plan — copy format cột Sale No: dòng chính đậm + caption phụ. */}
                    <TableCell sx={{ verticalAlign: 'middle' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.74rem' }}>
                        {s.planRef || '—'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.64rem' }}
                      >
                        {s.planRefSub}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'middle' }}>
                      <Chip
                        size="small"
                        label={s.matchType === 'direct' ? 'Direct' : 'Instruction'}
                        sx={{
                          height: 18,
                          fontSize: '0.62rem',
                          fontWeight: 700,
                          bgcolor: alpha(ACCENT_DO, 0.12),
                          color: ACCENT_DO,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'middle', fontSize: '0.74rem' }}>
                      {s.matchId ?? '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.74rem' }}>
                      {fmtNum(s.planKg)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.74rem' }}>
                      {fmtNum(allocated)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 700, fontSize: '0.74rem', color: remColor }}
                    >
                      {fmtNum(Math.abs(remaining))}
                      {remaining < 0 ? ' over' : ''}
                    </TableCell>
                    {/* Tách plan này thành 1 dòng DO mới ở block Add DO. */}
                    <TableCell sx={{ px: 0.5 }}>
                      <Button
                        size="small"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => addRow(i)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          color: ACCENT_DO,
                          minWidth: 0,
                          px: 0.75,
                        }}
                      >
                        DO
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Total DO Qty phải đúng bằng Plan Qty mỗi plan mới cho Save. */}
        {unbalancedPlans.length > 0 ? (
          <Alert severity="error" sx={{ mb: 1.5, py: 0.25 }}>
            <Typography variant="caption">
              <b>Total DO Qty must equal Plan Qty</b> for:{' '}
              {unbalancedPlans
                .map(
                  (x) =>
                    `${x.s.saleNo} (${x.diff > 0 ? 'over' : 'short'} ${fmtNum(Math.abs(x.diff))} kg)`,
                )
                .join(', ')}
              .
            </Typography>
          </Alert>
        ) : null}

        {/* ───────── Block 2: Add DO (editable, add/remove rows) ─────────
            Thêm dòng DO bằng nút "+ DO" ở từng dòng Plan (block trên) → Sale No
            follow plan đó, read-only. */}
        <Typography
          variant="overline"
          sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.5, display: 'block', mb: 0.5 }}
        >
          Add DO
        </Typography>
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {/* minWidth = tổng width cột — giữ scroll ngang trong container trên xs. */}
          <Table size="small" sx={{ minWidth: 1174, '& .MuiTableCell-root': { py: 0.25 } }}>
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    fontWeight: 800,
                    fontSize: '0.7rem',
                    whiteSpace: 'nowrap',
                    bgcolor: alpha(ACCENT_DO, 0.07),
                  },
                }}
              >
                <TableCell sx={{ width: 180 }}>Sale No</TableCell>
                <TableCell sx={{ width: 240 }}>From WH</TableCell>
                <TableCell sx={{ width: 140 }}>DO No</TableCell>
                <TableCell align="right" sx={{ width: 110 }}>DO Qty</TableCell>
                <TableCell sx={{ width: 260 }}>Note</TableCell>
                <TableCell sx={{ width: 110 }}>Match Type</TableCell>
                <TableCell sx={{ width: 90 }}>Match ID</TableCell>
                <TableCell sx={{ width: 44 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const plan = seeds[r.planId]
                return (
                <TableRow key={r.id}>
                  {/* Sale No — read-only, follow theo Plan (copy format cột Sale No). */}
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.74rem' }}>
                      {plan?.saleNo ?? '—'}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.64rem' }}
                    >
                      {plan?.buyer ?? ''}
                    </Typography>
                  </TableCell>
                  {/* From WH — per-seed override: plan.fromWhEditable quyết định
                      (direct=edit Shipper / instruct=read-only kho TLC); fallback scenario. */}
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    {!(plan?.fromWhEditable ?? scenario === 'direct') ? (
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>
                        {r.toWh || '—'}
                      </Typography>
                    ) : (
                      <Autocomplete
                        freeSolo
                        size="small"
                        options={whOptions}
                        loading={whQuery.isFetching}
                        value={r.toWh}
                        onChange={(_e, v) => patch(r.id, 'toWh', v ?? '')}
                        onInputChange={(_e, v) => patch(r.id, 'toWh', v)}
                        renderInput={(p) => <TextField {...p} size="small" sx={cellInputSx} />}
                      />
                    )}
                  </TableCell>
                  {/* DO No — đã book (edit) hiện số; chưa có → book mới khi Save. */}
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    {r.doNo ? (
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
                        {r.doNo}
                      </Typography>
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: '0.68rem' }}
                      >
                        Booked new on Save
                      </Typography>
                    )}
                  </TableCell>
                  {/* DO Qty — number, edit */}
                  <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                    <TextField
                      size="small"
                      // Format Standard: thousands separator, căn phải (0 decimals —
                      // đồng bộ module Matching, Plan Qty cũng integer).
                      value={r.doQty ? fmtNum(r.doQty) : ''}
                      onChange={(e) => {
                        const n = Number(e.target.value.replace(/[^\d]/g, ''))
                        patch(r.id, 'doQty', Number.isFinite(n) ? n : 0)
                      }}
                      error={!(r.doQty > 0)}
                      inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                      sx={cellInputSx}
                    />
                  </TableCell>
                  {/* Note — text */}
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    <TextField
                      size="small"
                      value={r.note}
                      onChange={(e) => patch(r.id, 'note', e.target.value)}
                      sx={cellInputSx}
                      fullWidth
                    />
                  </TableCell>
                  {/* Match Type — read-only, theo plan đang chọn. */}
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    <Chip
                      size="small"
                      label={plan?.matchType === 'direct' ? 'Direct' : 'Instruction'}
                      sx={{
                        height: 18,
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        bgcolor: alpha(ACCENT_DO, 0.12),
                        color: ACCENT_DO,
                      }}
                    />
                  </TableCell>
                  {/* Match ID — read-only, theo plan đang chọn. */}
                  <TableCell sx={{ verticalAlign: 'middle', fontSize: '0.74rem' }}>
                    {plan?.matchId ?? '—'}
                  </TableCell>
                  {/* Xoá dòng DO */}
                  <TableCell sx={{ verticalAlign: 'middle', px: 0.5 }}>
                    <Tooltip title="Remove DO row">
                      <IconButton
                        size="small"
                        onClick={() => removeRow(r.id)}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                )
              })}
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ py: 1.5, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        No DO rows. Press <b>+ DO</b> on a plan row above to create one.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {rows.length} DO • Total DO Qty: <b>{fmtNum(totalQty)}</b> kg
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 1.5, py: 1 }}>
        <Button onClick={onClose} color="inherit" disabled={savingDo}>
          Cancel
        </Button>
        {/* Save DO — book từng dòng vào delivery_order_scs (mang theo match_type/match_id). */}
        <Button
          variant="contained"
          startIcon={
            savingDo ? (
              <CircularProgress size={14} sx={{ color: '#fff' }} />
            ) : (
              <SaveOutlinedIcon />
            )
          }
          disabled={!canSaveDo}
          onClick={handleSaveDo}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: ACCENT_DO,
            color: '#fff',
            '&:hover': { bgcolor: alpha(ACCENT_DO, 0.85) },
            '&.Mui-disabled': { bgcolor: alpha(ACCENT_DO, 0.3), color: '#fff' },
          }}
        >
          Save DO
        </Button>
      </DialogActions>
    </>
  )
}
